import getArea from "./getArea";
import parsePhone, { isForeignPhone, toLocalPhone } from "./parsePhone";

const MIN_PREFIJO_LENGTH = 2;
const MAX_AREACODE_LENGTH = 5;
const FIXED_PHONES_DIGITS_LENGTH = 11;
const MOBILE_PHONES_DIGITS_LENGTH = 13;
const NATIONAL_DIGITS_LENGTH = 10;
const MOBILE_PREFIX = "15";
/** 549 + área + 15 + número: 54 9 341 15 522 7905. */
const INTERNATIONAL_MAX_DIGITS_LENGTH = 15;

/**
 * Lo que queda guardado en el campo a partir de lo que se tipeó o pegó: con 0
 * y, si es celular, con 15 después del área. El 0 y el 15 se agregan solos
 * pero nunca ocupan el lugar de un dígito tipeado.
 *
 * Un 1 justo después del área queda solo hasta la tecla siguiente, que dice si
 * es el 15 o el primer dígito del número.
 */
export const normalizePhoneInput = (
  value: string,
  isMobile: boolean
): string => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";

  // Ningún código de área empieza con 5: es un 54 tipeado o pegado, que se
  // deja como está hasta que se pueda interpretar entero. Igual que al tipear
  // sin 54, un 15 después del área es el de celular y todavía faltan dígitos.
  if (digits.startsWith("5")) {
    const phone = parsePhone(digits, isMobile);
    const subscriber = phone?.number.substring(phone.area.Code.length - 1);
    const waitingForSubscriber =
      digits.length < INTERNATIONAL_MAX_DIGITS_LENGTH &&
      subscriber?.startsWith(MOBILE_PREFIX);

    return phone && !waitingForSubscriber
      ? toLocalPhone({ ...phone, isMobile })
      : digits.substring(0, INTERNATIONAL_MAX_DIGITS_LENGTH);
  }

  const number = digits.startsWith("0") ? digits : `0${digits}`;
  const area = getArea(number);

  if (!area)
    return number.substring(
      0,
      isMobile ? MOBILE_PHONES_DIGITS_LENGTH : FIXED_PHONES_DIGITS_LENGTH
    );

  const subscriberLength = NATIONAL_DIGITS_LENGTH - (area.Code.length - 1);
  const rest = number.substring(area.Code.length);

  if (!isMobile) return area.Code + rest.substring(0, subscriberLength);

  if (rest === "" || rest === "1") return area.Code + rest;

  const subscriber = rest.startsWith(MOBILE_PREFIX)
    ? rest.substring(MOBILE_PREFIX.length)
    : rest;

  return area.Code + MOBILE_PREFIX + subscriber.substring(0, subscriberLength);
};

/** El texto que se ve en el campo: (0341) 15 522-7905 o (0341) 722-7965. */
export const formatPhone = (number: string, isMobile: boolean): string => {
  const area = getArea(number);

  if (!area)
    return number &&
      !number.startsWith("5") &&
      number.length <= MAX_AREACODE_LENGTH
      ? `(${number}`
      : number;

  let text = `(${area.Code})`;
  let rest = number.substring(area.Code.length);
  if (!rest) return text;

  if (isMobile) {
    text += ` ${rest.substring(0, MOBILE_PREFIX.length)}`;
    rest = rest.substring(MOBILE_PREFIX.length);
    if (!rest) return text;
  }

  const prefijoLength =
    MIN_PREFIJO_LENGTH + (MAX_AREACODE_LENGTH - area.Code.length);

  text += ` ${rest.substring(0, prefijoLength)}`;
  if (rest.length > prefijoLength) text += `-${rest.substring(prefijoLength)}`;

  return text;
};

/** El teléfono como lo edita PhoneInput junto con el selector de tipo. */
export interface PhoneField {
  /** Con 0 y 15, o lo escrito si no se entiende. */
  number: string;
  isMobile: boolean;
}

/** Lo guardado, como lo edita la máscara. Lo que no se entiende queda como está. */
export const toPhoneField = (
  value?: string | null,
  onlyMobile?: boolean,
  defaultIsMobile: boolean = true
): PhoneField => {
  const phone = parsePhone(value, defaultIsMobile);
  // Hay datos guardados como number aunque el tipo diga string.
  if (!phone) return { number: String(value ?? ""), isMobile: defaultIsMobile };

  const isMobile = onlyMobile || phone.isMobile;
  return { number: toLocalPhone({ ...phone, isMobile }), isMobile };
};

/** El mismo número con otro tipo: la máscara le agrega o le saca el 15. */
export const withPhoneType = (
  { number, isMobile }: PhoneField,
  nextIsMobile: boolean
): PhoneField => {
  const phone = parsePhone(number, isMobile);
  return {
    number: phone ? toLocalPhone({ ...phone, isMobile: nextIsMobile }) : number,
    isMobile: nextIsMobile,
  };
};

/**
 * Lo que va al formulario: 549 o 54 si se entiende y, si no, lo que muestra la
 * máscara sin el 15 que agrega ella. Con el 15, a un celular al que le faltan
 * dos dígitos se lo leería como otro número completo: (0341) 15 522-79 sería
 * 5493411552279. Un + sin dígitos es un campo vacío.
 */
export const toPhoneFieldValue = ({ number, isMobile }: PhoneField): string => {
  if (isForeignPhone(number) && !/\d/.test(number)) return "";

  const phone = parsePhone(number, isMobile);
  if (phone && toLocalPhone(phone) === number)
    return `${phone.isMobile ? "549" : "54"}${phone.number}`;

  // Un 54 o 549 a medio escribir: la máscara lo deja como se escribió, con el
  // 15, y se saca igual que en el número local.
  const international = /^(549?)(0?)(\d*)$/.exec(number);
  if (international) {
    const [, prefix, zero, rest] = international;
    const local = toPhoneFieldValue({ number: `0${rest}`, isMobile });
    // Si se interpretó entero, lo escrito ya tiene su 54: no se agrega otro.
    return local === `0${rest}` || !local.startsWith("0")
      ? number
      : prefix + zero + local.substring(1);
  }

  const area = getArea(number);
  return isMobile &&
    area &&
    number.substring(area.Code.length).startsWith(MOBILE_PREFIX)
    ? area.Code + number.substring(area.Code.length + MOBILE_PREFIX.length)
    : number;
};
